import type { BrandLogo, BrandRecord } from '@/types'
import parkezeMark from '@/assets/parkeze-mark.png'

/** The web prefers the SVG and falls back to the PNG (logbook decision 26). */
const logoSrc = (logo: BrandLogo) => logo.svg ?? logo.png

/**
 * An operator's logo that fails to load (a legacy hot-linked URL that moved,
 * say) is replaced by Parkeze's mark, bundled with the map so the fallback
 * itself cannot go missing.
 */
function fallBackToParkeze(e: React.SyntheticEvent<HTMLImageElement>) {
  const img = e.currentTarget
  if (img.src !== new URL(parkezeMark, window.location.href).href) img.src = parkezeMark
}

/**
 * Who leads a branded page: the operator's wordmark alone, with the name as
 * its alt text; else the operator's mark with the name beside it; else, with
 * no logo at all, Parkeze's mark with the name.
 *
 * Only the light variants are used: a branded page is always light
 * (hooks/use-branding.ts), so a dark variant would sit on the wrong
 * background. `height` is the logo's box in pixels; a wordmark is fitted
 * inside `maxWidth` with object-fit, which is the only sizing that works for
 * an SVG without intrinsic dimensions.
 */
export function BrandIdentity({ brand, height, maxWidth, nameClassName = '', centered = false }: {
  brand: BrandRecord
  height: number
  maxWidth: number
  nameClassName?: string
  /** Center a wordmark in its box (the verification screen) instead of anchoring it left (the header). */
  centered?: boolean
}) {
  const { wordmark, mark } = brand.logo

  if (wordmark) {
    return (
      <div style={{ height, maxWidth, width: maxWidth, flexShrink: 1, minWidth: 0 }}>
        <img
          src={logoSrc(wordmark)}
          alt={brand.name}
          onError={fallBackToParkeze}
          style={{ height: '100%', width: '100%', objectFit: 'contain', objectPosition: centered ? 'center' : 'left center', display: 'block' }}
        />
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2 min-w-0">
      <img
        src={mark ? logoSrc(mark) : parkezeMark}
        alt=""
        onError={fallBackToParkeze}
        style={{ height, width: height, objectFit: 'contain', flexShrink: 0, display: 'block' }}
      />
      <span className={`font-display font-bold tracking-tight truncate ${nameClassName}`}>{brand.name}</span>
    </div>
  )
}
